import {Stack} from 'src/design/atoms/Stack';
import {Title} from 'src/design/atoms/Title';
import React from 'react';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import type {IFormFieldV2} from 'src/types/FormV2';

const sampleFormMeta: IFormFieldV2[] = [
  {
    name: 'employmentType',
    invariantId: 'employmentType',
    label: 'Employment Type',
    type: 'select',
    required: true,
    options: [
      {id: 'fulltime', label: 'Full Time'},
      {id: 'contractor', label: 'Contractor'},
      {id: 'intern', label: 'Intern'},
    ],
    description: 'Select your employment type',
  },
  {
    name: 'contractorDetails',
    label: 'Contractor Details',
    type: 'text',
    required: true,
    description: 'Enter your contractor ID or company name',
    visibility: {
      isDefaultHidden: true,
      rules: [
        {
          offlineId: '1',
          targetFieldInvariantId: 'employmentType',
          fieldValue: 'contractor',
          visibility: true,
        },
      ],
    },
  },
  {
    name: 'internshipDuration',
    label: 'Internship Duration (months)',
    type: 'number',
    required: true,
    description: 'Enter the duration of your internship',
    visibility: {
      isDefaultHidden: true,
      rules: [
        {
          offlineId: '1',
          targetFieldInvariantId: 'employmentType',
          fieldValue: 'intern',
          visibility: true,
        },
      ],
    },
  },
  {
    name: 'hasHealthInsurance',
    invariantId: 'hasHealthInsurance',
    label: 'Health Insurance Required',
    type: 'switch',
    required: true,
    description: 'Do you need health insurance coverage?',
    visibility: {
      isDefaultHidden: true,
      rules: [
        {
          offlineId: '1',
          targetFieldInvariantId: 'employmentType',
          fieldValue: 'fulltime',
          visibility: true,
        },
        {
          offlineId: '2',
          targetFieldInvariantId: 'employmentType',
          fieldValue: 'contractor',
          visibility: true,
        },
      ],
    },
  },
  {
    name: 'insurancePlan',
    label: 'Insurance Plan',
    type: 'select',
    required: true,
    options: [
      {id: 'basic', label: 'Basic Coverage'},
      {id: 'premium', label: 'Premium Coverage'},
      {id: 'family', label: 'Family Coverage'},
    ],
    description: 'Select your preferred insurance plan',
    visibility: {
      isDefaultHidden: true,
      rules: [
        {
          offlineId: '1',
          targetFieldInvariantId: 'hasHealthInsurance',
          fieldValue: 'true',
          visibility: true,
        },
      ],
    },
  },
  {
    name: 'firstName',
    label: 'First Name',
    type: 'text',
    size: 'md',
    required: true,
    validation: {
      minLength: 2,
      maxLength: 50,
      pattern: '^[a-zA-Z\\s]+$',
      patternError: 'First name must contain only letters and spaces',
    },
    description: 'Enter your first name as it appears on official documents',
  },
  {
    name: 'lastName',
    label: 'Last Name',
    type: 'text',
    size: 'md',
    required: true,
    validation: {
      minLength: 2,
      maxLength: 50,
      pattern: '^[a-zA-Z\\s]+$',
      patternError: 'Last name must contain only letters and spaces',
    },
    description: 'Enter your last name as it appears on official documents',
  },
  {
    name: 'age',
    label: 'Age',
    type: 'number',
    size: 'xs',
    required: true,
    validation: {
      min: 18,
      max: 120,
      customError: 'Age must be between 18 and 120',
    },
    description: 'You must be at least 18 years old',
  },
  {
    name: 'birthDate',
    label: 'Birth Date',
    type: 'date',
    size: 'lg',
    required: true,
    description: 'Select your date of birth',
  },
  {
    name: 'bio',
    label: 'Biography',
    type: 'textarea',
    validation: {
      maxLength: 500,
    },
    description: 'Tell us about yourself (optional)',
  },
  {
    name: 'banner',
    label: 'Banner',
    type: 'richtext',
    description: 'Profile Banner',
  },
  {
    name: 'department',
    label: 'Department',
    type: 'select',
    required: true,
    options: [
      {id: 'engineering', label: 'Engineering'},
      {id: 'marketing', label: 'Marketing'},
      {id: 'sales', label: 'Sales'},
      {id: 'hr', label: 'Human Resources'},
    ],
    description: 'Select your department',
  },
  {
    name: 'responsibilities',
    label: 'Responsibilities',
    type: 'select-multiple',
    required: true,
    options: [
      {id: 'coding', label: 'Software Development'},
      {id: 'testing', label: 'Quality Assurance'},
      {id: 'design', label: 'UI/UX Design'},
      {id: 'docs', label: 'Documentation'},
      {id: 'mentoring', label: 'Team Mentoring'},
      {id: 'planning', label: 'Project Planning'},
    ],
    description: 'Select all responsibilities that apply to your role',
  },
  {
    name: 'skills',
    label: 'Skills',
    type: 'autocomplete',
    required: true,
    options: [
      {id: 'react', label: 'React'},
      {id: 'typescript', label: 'TypeScript'},
      {id: 'javascript', label: 'JavaScript'},
      {id: 'python', label: 'Python'},
      {id: 'java', label: 'Java'},
      {id: 'csharp', label: 'C#'},
      {id: 'sql', label: 'SQL'},
      {id: 'aws', label: 'AWS'},
    ],
    description: 'Search and select your primary skill',
  },
  {
    name: 'assignedAsset',
    label: 'Assigned Asset',
    type: 'asset',
    required: true,
    description: 'Select the primary asset assigned to you',
    assetOptions: {
      includeLeaves: true,
      requireLeaf: true,
    },
  },
  {
    name: 'supervisor',
    label: 'Supervisor',
    type: 'person',
    required: true,
    description: 'Select your direct supervisor',
    personOptions: {
      hideSelectAll: true,
    },
  },
  {
    name: 'teamMembers',
    label: 'Team Members',
    type: 'person-select-multiple',
    required: true,
    description: 'Select all team members that report to you',
    personOptions: {
      hideSelectAll: false,
    },
  },
  {
    name: 'categories',
    label: 'Categories',
    type: 'select-multiple',
    required: true,
    options: [
      {id: 'frontend', label: 'Frontend Development'},
      {id: 'backend', label: 'Backend Development'},
      {id: 'devops', label: 'DevOps'},
      {id: 'qa', label: 'Quality Assurance'},
      {id: 'design', label: 'Design'},
      {id: 'pm', label: 'Project Management'},
    ],
    description: 'Select all relevant categories',
  },
  {
    name: 'documents',
    label: 'Supporting Documents',
    type: 'file',
    required: true,
    description: 'Upload any relevant documents (PDF only, max 10MB)',
    fileUploadOptions: {
      allowMultiple: false,
      allowedFileTypes: ['.pdf', 'application/pdf'],
      maxFileSize: 10 * 1024 * 1024, // 10MB in bytes
      isHideUploadSuccessEnabled: false,
    },
  },
  {
    name: 'attachments',
    label: 'Additional Attachments',
    type: 'file',
    required: false,
    description: 'Upload any supporting files (PDF, DOC, XLS - max 5MB each)',
    fileUploadOptions: {
      allowMultiple: true,
      allowedFileTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ],
      maxFileSize: 5 * 1024 * 1024, // 5MB in bytes
      isHideUploadSuccessEnabled: false,
    },
  },
  {
    name: 'satisfaction',
    label: 'Job Satisfaction',
    type: 'scale',
    required: true,
    validation: {
      min: 1,
      max: 5,
    },
    description: 'Rate your job satisfaction from 1 to 5',
  },
  {
    name: 'isActive',
    label: 'Active Status',
    type: 'switch',
    size: 'md',
    required: true,
    description: 'Toggle the active status',
    switchOptions: {
      positiveText: 'Active',
      negativeText: 'Inactive',
    },
  },
  {
    name: 'isPublic',
    label: 'Visibility',
    type: 'switch',
    size: 'md',
    required: false,
    description: 'Control whether this item is publicly visible',
    switchOptions: {
      positiveText: 'Public',
      negativeText: 'Private',
    },
  },
  {
    name: 'termsAccepted',
    label: 'Terms and Conditions',
    type: 'switch',
    required: true,
    description: 'I agree to the terms and conditions',
  },
  {
    name: 'contactPhone',
    label: 'Contact Phone',
    type: 'phone',
    required: true,
    description: 'Enter your primary contact phone number',
    validation: {
      pattern: '^[0-9-+()\\s]*$',
      patternError: 'Please enter a valid phone number',
    },
  },
  {
    name: 'signature',
    label: 'Digital Signature',
    type: 'signature',
    required: true,
    description: 'Please sign to confirm your submission',
  },
  {
    name: 'section1',
    label: 'Additional Information',
    type: 'heading',
  },
  {
    name: 'section1Info',
    label: 'Important Note',
    type: 'paragraph',
    description:
      'Please review all information carefully before submitting. Once submitted, you cannot modify your responses.',
  },
  {
    name: 'divider1',
    type: 'divider',
    label: '',
  },
  {
    name: 'subform1',
    label: 'Additional Form',
    type: 'subform',
    required: true,
    description: 'Please fill out the additional form',
    subFormOptions: {
      subFormDefinitionId: 123,
      showPreviewComponent: true,
    },
  },
  {
    name: 'email',
    label: 'Email Address',
    type: 'text',
    required: true,
    description: 'Enter your work email address',
    textboxOptions: {
      validationType: 'email',
    },
  },
  {
    name: 'website',
    label: 'Personal Website',
    type: 'text',
    required: false,
    description: 'Enter your personal website URL',
    textboxOptions: {
      validationType: 'url',
    },
  },
  {
    name: 'mailingAddress',
    label: 'Mailing Address',
    type: 'textarea',
    required: true,
    description: 'Enter your mailing address',
    textboxOptions: {
      validationType: 'address',
    },
  },
  {
    name: 'hourlyRate',
    label: 'Hourly Rate',
    type: 'text',
    required: true,
    description: 'Enter your hourly rate',
    textboxOptions: {
      validationType: 'currency',
    },
    visibility: {
      isDefaultHidden: true,
      rules: [
        {
          offlineId: '1',
          targetFieldInvariantId: 'employmentType',
          fieldValue: 'contractor',
          visibility: true,
        },
      ],
    },
  },
  {
    name: 'availabilityTime',
    label: 'Daily Availability',
    type: 'text',
    required: true,
    description: 'Enter your availability time (24-hour format)',
    textboxOptions: {
      validationType: 'time',
    },
  },
];

export const ProactFormEngineExample: React.FC = () => {
  const form = useProactForm({formMeta: sampleFormMeta});

  const handleSubmit = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      console.log('Form is valid:', form.getFieldValues());
    } else {
      console.log('Form has validation errors');
    }
  };

  return (
    <Stack>
      <Title order={3}>Proact Form Engine with Visibility Rules</Title>
      <ProactForm form={form}>
        <ProactFormGrid />
      </ProactForm>
      <SaveButton onClick={handleSubmit}>Submit</SaveButton>
    </Stack>
  );
};
